import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Goals() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", target_amount: 1000, deadline: "", category: "Vacation" });

  const load = async () => {
    const { data } = await api.get("/lifestyle/goals");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return;
    try {
      await api.post("/lifestyle/goals", { ...form, target_amount: parseFloat(form.target_amount) });
      toast.success("Goal created");
      setForm({ title: "", target_amount: 1000, deadline: "", category: "Vacation" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Couldn't create");
    }
  };

  const contribute = async (g) => {
    const amt = window.prompt("Add how much from your cashback?", "25");
    if (!amt) return;
    await api.put(`/lifestyle/goals/${g.id}/contribute?amount=${parseFloat(amt)}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/lifestyle/goals/${id}`);
    load();
  };

  return (
    <div data-testid="page-goals">
      <PageHeader
        eyebrow="Savings Goals"
        title="Cashback that compounds toward what matters"
        subtitle="Set goals — vacations, school, emergency fund — and route your cashback there."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="add-goal-btn"><Plus className="w-4 h-4 mr-2" /> New goal</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create savings goal</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="mb-1.5 block">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Disney World 2026" data-testid="goal-title" /></div>
                <div><Label className="mb-1.5 block">Target amount ($)</Label><Input type="number" min="1" step="50" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} data-testid="goal-target" /></div>
                <div><Label className="mb-1.5 block">Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Vacation, Emergency, Holidays…" /></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800 text-white">Create goal</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((g) => {
          const pct = Math.min(100, ((g.progress || 0) / (g.target_amount || 1)) * 100);
          return (
            <Card key={g.id} className="border-gray-200 shadow-none rounded-2xl" data-testid={`goal-${g.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">{g.category || "Goal"}</div>
                    <h3 className="font-display font-bold text-lg">{g.title}</h3>
                  </div>
                  <button onClick={() => remove(g.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm text-gray-600">{fmtMoney(g.progress || 0)} of {fmtMoney(g.target_amount)}</div>
                <Progress value={pct} className="mt-2 h-2" />
                <div className="mt-2 text-xs text-gray-500">{pct.toFixed(0)}% complete</div>
                <Button size="sm" onClick={() => contribute(g)} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid={`contribute-${g.id}`}>
                  Contribute from cashback
                </Button>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full">
            <Card className="border-gray-200 shadow-none rounded-2xl">
              <CardContent className="p-12 text-center">
                <Target className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <h3 className="font-display font-bold text-lg">No goals yet</h3>
                <p className="text-gray-600 text-sm mt-1">Set your first savings goal to start routing cashback to it.</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
